<!--
  VLocation

  還原自 index.pretty.js L2547。render function 已轉回 template，
  巢狀 callback 已整理為具名函式。

  取餐方式切換對話框：外送／自取二選一。
  選外送時輸入地址，按「完成」後以 Google Geocoder + DirectionsService
  重算距離與運費，結果寫入 localStorage 供結帳頁使用。

  運費規則（與 Home.vue 相同）：
    在外送範圍多邊形內 -> 固定 200 元
    在範圍外           -> 每公里 100 元
-->

<template>
  <div class="bg-overlay align-center d-flex jc-center fd-row p-flex t-0 r-0 b-0 l-0">
    <div class="pop-container d-flex p-flex a-center jc-center" @click="documentClick">
      <div ref="popMenu" class="card normal">
        <div class="header-container">
          <div class="d-flex jc-space">
            <div class="btn-group d-flex">
              <button
                class="btn btn-white btn-round-left"
                :class="{ actived: order_type === 'ORDER_TYPE_DELIVERY' }"
                @click="set_order_type('ORDER_TYPE_DELIVERY')"
              >
                <p class="t-align-center">{{ $t('delivery') }}</p>
              </button>

              <button
                class="btn btn-white btn-round-right"
                :class="{ actived: order_type === 'ORDER_TYPE_PICK_UP' }"
                @click="set_order_type('ORDER_TYPE_PICK_UP')"
              >
                <p class="t-align-center">{{ $t('pick_up') }}</p>
              </button>
            </div>

            <div>
              <i class="font-28 material-icons hover t-gray-7 c-pointer" @click="close">close</i>
            </div>
          </div>
        </div>

        <div class="footer-container">
          <div
            v-if="order_type === 'ORDER_TYPE_DELIVERY'"
            class="mui-textfield m-b-24"
            :class="{ is_filled: delivery.location.length > 0 }"
          >
            <GmapAutocomplete
              placeholder=""
              :value="delivery.location"
              @place_changed="set_place"
              @input="get_place"
            />
            <label tabindex="-1">{{ $t('input_location') }}</label>
          </div>

          <div v-else class="m-b-24 d-flex a-center">
            <p class="title font-18 t-gray-3 m-r-12">{{ $t('where') }}: </p>
            <p class="title font-18 t-gray-3">{{ $t('location') }}</p>
          </div>

          <div class="d-flex jc-space a-center">
            <button
              class="btn btn-fill"
              :class="apply_class"
              :disabled="!apply_enable"
              @click="done"
            >
              <p class="t-gray-10 font-18">{{ $t('done') }}</p>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 隱藏的 GmapMap：僅為載入 Google Maps SDK，done() 需要 Geocoder 與 DirectionsService -->
    <div v-show="false">
      <GmapMap
        ref="mapRef"
        class="card-map"
        :center="get_center"
        :zoom="13"
        :options="options"
        map-type-id="roadmap"
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import * as types from '@/store/mutation-types'
import { isOutsideDeliveryArea, setStorage } from '@/utils'

const STORE_LOCATION = { lat: 25.0142841153413, lng: 121.21120369949432 }

const KEY_ROUTES = '5ew41gf56ew456-routes'
const KEY_ROUTES_RESPONSE = '5ew41gf56ew456-routes-response'
const MONTH = 2592000

export default {
  data() {
    return {
      order_type: this.getOrderType(),
      location_verify: this.getLocation() !== '',

      delivery: {
        location: this.getLocation(),
        start_location: { ...STORE_LOCATION },
        end_location: null,
        center: { ...STORE_LOCATION },
        distance: 0,
        fee: 0,
        time: 0,
        place: null
      },

      options: {
        zoomControl: false,
        mapTypeControl: false,
        scaleControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        rotateControl: false,
        disableDefaultUi: true,
        clickableIcons: false
      }
    }
  },

  computed: {
    apply_enable() {
      return this.order_type === 'ORDER_TYPE_PICK_UP' || this.location_verify
    },

    apply_class() {
      return this.apply_enable ? 'btn-primary' : 'btn-grey'
    },

    get_center() {
      return this.delivery.center
    }
  },

  methods: {
    ...mapActions(['setLocation', 'setOrderType']),
    ...mapGetters(['getLocation', 'getOrderType']),

    /** 點在對話框外就關閉 */
    documentClick(event) {
      const menu = this.$refs.popMenu
      if (menu === undefined) return

      const target = event.target
      if (menu !== target && !menu.contains(target)) this.close()
    },

    close() {
      this.$emit('event', 'location_close')
    },

    set_place(place) {
      if (place && place.formatted_address) {
        this.delivery.location = place.formatted_address
        this.location_verify = true
      }
    },

    get_place(event) {
      this.delivery.location = event.target.value
      this.location_verify = false
    },

    set_order_type(type) {
      this.order_type = type
    },

    done() {
      // 自取：直接套用並關閉
      if (this.order_type !== types.ORDER_TYPE_DELIVERY) {
        this.setOrderType(this.order_type)
        this.$emit('event', 'location_update')
        return
      }

      if (this.delivery.location === '') return

      this.setOrderType(this.order_type)

      // 地址沒變就不用重算路線
      if (this.delivery.location === this.getLocation()) {
        this.$emit('event', 'location_update')
        return
      }

      this.setLocation(this.delivery.location)
      this.geocodeAndRoute()
    },

    /** 地址 -> 座標 -> 路線 -> 距離與運費 */
    geocodeAndRoute() {
      const geocoder = new window.google.maps.Geocoder()

      geocoder.geocode({ address: this.delivery.location }, (results, status) => {
        if (status !== 'OK') {
          this.delivery.place = null
          this.$emit('event', 'location_update')
          return
        }

        this.delivery.place = results[0]
        this.delivery.end_location = {
          lat: results[0].geometry.location.lat(),
          lng: results[0].geometry.location.lng()
        }

        this.delivery.center.lat = parseFloat(
          ((this.delivery.start_location.lat + this.delivery.end_location.lat) / 2).toFixed(14)
        )
        this.delivery.center.lng = parseFloat(
          ((this.delivery.start_location.lng + this.delivery.end_location.lng) / 2).toFixed(14)
        )

        this.route()
      })
    },

    route() {
      const directions = new window.google.maps.DirectionsService()
      const request = {
        origin: this.delivery.start_location,
        destination: this.delivery.end_location,
        travelMode: 'DRIVING'
      }

      directions.route(request, (response, status) => {
        if (status !== 'OK') {
          this.$emit('event', 'location_update')
          return
        }

        this.delivery.time = (response.routes[0].legs[0].duration.value / 60) | 0

        if (isOutsideDeliveryArea(this.delivery.end_location)) {
          /*
           * 不足一公里以一公里計 —— 店家的實際規則。
           * 原本是 Math.round，7.4 公里會算成 700 元，但實際要 800 元。
           * 後端 order_server.js 的 deliveryMinimumSpend 用同一個規則。
           *
           * delivery.distance 是「計費公里數」，同時也是 10 公里上限
           * （MAX_DELIVERY_DISTANCE_KM / MAX_DELIVERY_KM）的比較對象，
           * 所以改成 ceil 之後 10.1~10.9 公里的地址會從「可下單、低消
           * 1000」變成「超出服務範圍」。這與「不足一公里算一公里」
           * 一致 —— 11 公里本來就超過 10 公里上限。
           */
          this.delivery.distance = Math.ceil(response.routes[0].legs[0].distance.value / 1000)
          this.delivery.fee = 100 * this.delivery.distance
        } else {
          this.delivery.distance = 0
          this.delivery.fee = 200
        }

        setStorage(KEY_ROUTES, this.delivery, MONTH)
        setStorage(KEY_ROUTES_RESPONSE, response, MONTH)

        this.$emit('event', 'location_update')
      })
    }
  },

  created() {},

  destroyed() {},

  mounted() {}
}
</script>
